import React, { useEffect, useRef, useState } from "react";
import {
  Input,
  InputNumber,
  Radio,
  Checkbox,
  DatePicker,
  Select,
  Button,
  Typography,
  Space,
  Flex,
  Tabs,
  Row,
  Col,
  Slider,
  Table,
  Modal,
  message,
  theme,
} from "antd";
import ClinicalFormFileUpload from "./ClinicalFormFileUpload";
import ClinicalFormDateTime from "./ClinicalFormDateTime";
import dayjs from "dayjs";
import { FIELD_TYPES } from "../../domain/clinicalForms";
import btnStyles from "../../styles/Custom/Button.module.css";
import {
  findMissingRequired,
  formatRequiredError,
} from "./validateRequired";
import {
  DECIMAL_ERROR_MESSAGE,
  buildDecimalInputHandlers,
  formatNumericRange,
  stripNonDecimalChars,
} from "./numericField";
import { MOBILE_DATE_PICKER_PROPS } from "../IpEMR/Nurse/CareItems/nurseCareMobilePickerProps";
import "../IpEMR/ipforms.css";

const TINT_BG = "color-mix(in srgb, var(--ant-primary-color) 8%, white)";
/** Matches static IA History comments input (Habits.js). */
const FIELD_CONTROL_WIDTH = { width: "60%", maxWidth: "100%" };
const COMPACT_CONTROL_WIDTH = { width: 260, maxWidth: "100%" };

const optionsToAntd = (opts) =>
  (opts || []).map((o) =>
    typeof o === "string"
      ? { label: o, value: o }
      : { label: o.label, value: o.value }
  );

const toDayjs = (v) => (v ? dayjs(v) : null);

const cellOptions = (col) => optionsToAntd(col.options);

const renderTableCell = (col, value, onChange, readOnly, uploadContext) => {
  switch (col.type) {
    case "textarea":
      return (
        <Input.TextArea
          size="small"
          autoSize={{ minRows: 1, maxRows: 4 }}
          value={value ?? ""}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "number":
      return (
        <InputNumber
          size="small"
          value={value ?? null}
          disabled={readOnly}
          onChange={(v) => onChange(v)}
          parser={stripNonDecimalChars}
          style={{ width: "100%" }}
        />
      );
    case "date":
      return (
        <DatePicker
          size="small"
          value={toDayjs(value)}
          disabled={readOnly}
          onChange={(d) => onChange(d ? d.toISOString() : null)}
          style={{ width: "100%" }}
          inputReadOnly
          {...MOBILE_DATE_PICKER_PROPS}
        />
      );
    case "datetime":
      return (
        <ClinicalFormDateTime
          size="small"
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          style={{ width: "100%" }}
        />
      );
    case "select":
      return (
        <Select
          size="small"
          value={value ?? undefined}
          options={cellOptions(col)}
          onChange={onChange}
          allowClear
          disabled={readOnly}
          style={{ width: "100%" }}
        />
      );
    case "multiselect":
      return (
        <Select
          size="small"
          mode="multiple"
          value={Array.isArray(value) ? value : []}
          options={cellOptions(col)}
          onChange={onChange}
          allowClear
          disabled={readOnly}
          style={{ width: "100%" }}
        />
      );
    case "checkbox": {
      const selected = Array.isArray(value) ? value : [];
      return (
        <Checkbox.Group
          value={selected}
          options={cellOptions(col)}
          onChange={onChange}
          disabled={readOnly}
        />
      );
    }
    case "radio":
      return (
        <Radio.Group
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly}
        >
          {(col.options || []).map((opt) => (
            <Radio key={opt.value} value={opt.value}>
              {opt.label}
            </Radio>
          ))}
        </Radio.Group>
      );
    case "file":
      return (
        <ClinicalFormFileUpload
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          admissionId={uploadContext?.admissionId}
          admissionType={uploadContext?.admissionType}
        />
      );
    case "text":
    default:
      return (
        <Input
          size="small"
          value={value ?? ""}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

const renderControl = (
  field,
  value,
  onChange,
  readOnly,
  uploadContext,
  errorContext
) => {
  switch (field.type) {
    case FIELD_TYPES.TEXT:
      return (
        <Input.TextArea
          rows={2}
          placeholder={field.placeholder || "Your answer"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly}
          style={FIELD_CONTROL_WIDTH}
        />
      );
    case FIELD_TYPES.TEXTAREA:
      return (
        <Input.TextArea
          rows={2}
          placeholder={field.placeholder || "Your answer"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly}
          style={FIELD_CONTROL_WIDTH}
        />
      );
    case FIELD_TYPES.NUMBER: {
      const rangeHint = formatNumericRange({
        min: field.min,
        max: field.max,
      });
      const decimalHandlers = buildDecimalInputHandlers({
        onError: (msg) => errorContext?.set(msg),
        onValid: () => errorContext?.clear(),
      });
      return (
        <Space direction="vertical" size={4} style={COMPACT_CONTROL_WIDTH}>
          <InputNumber
            {...decimalHandlers}
            placeholder={
              rangeHint ? `Enter ${rangeHint}` : field.placeholder || "0"
            }
            min={field.min}
            max={field.max}
            step={field.step ?? 1}
            style={{ width: "100%" }}
            value={value ?? null}
            onChange={(v) => {
              errorContext?.clear();
              onChange(v);
            }}
            disabled={readOnly}
          />
          {rangeHint && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Allowed range: {rangeHint}
            </Typography.Text>
          )}
        </Space>
      );
    }
    case FIELD_TYPES.DATE:
      return (
        <DatePicker
          style={{ width: "100%", maxWidth: 260 }}
          placeholder={field.placeholder || "Select date"}
          value={toDayjs(value)}
          onChange={(d) => onChange(d ? d.toISOString() : null)}
          disabled={readOnly}
          inputReadOnly
          {...MOBILE_DATE_PICKER_PROPS}
        />
      );
    case FIELD_TYPES.DATETIME:
      return (
        <ClinicalFormDateTime
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          placeholder={field.placeholder || "Select date & time"}
        />
      );
    case FIELD_TYPES.SELECT:
      return (
        <Select
          placeholder={field.placeholder || "Select"}
          options={optionsToAntd(field.options)}
          style={FIELD_CONTROL_WIDTH}
          value={value ?? undefined}
          onChange={onChange}
          allowClear
          disabled={readOnly}
        />
      );
    case FIELD_TYPES.MULTISELECT:
      return (
        <Select
          mode="multiple"
          placeholder={field.placeholder || "Select"}
          options={optionsToAntd(field.options)}
          style={FIELD_CONTROL_WIDTH}
          value={value ?? []}
          onChange={onChange}
          allowClear
          disabled={readOnly}
        />
      );
    case FIELD_TYPES.SLIDER: {
      const min = field.min ?? 0;
      const max = field.max ?? 10;
      const step = field.step ?? 1;
      const current = typeof value === "number" ? value : min;
      return (
        <Flex align="center" gap={16} style={{ width: "100%" }}>
          <Slider
            min={min}
            max={max}
            step={step}
            value={current}
            onChange={(v) => onChange(v)}
            disabled={readOnly}
            style={{ flex: 1 }}
          />
          <Typography.Text strong style={{ minWidth: 32, textAlign: "right" }}>
            {current}
          </Typography.Text>
        </Flex>
      );
    }
    case FIELD_TYPES.TABLE: {
      const cols = field.columns || [];
      const minRows = field.min_rows ?? 0;
      const maxRows = field.max_rows ?? 50;
      const allowAddRows = field.allow_add_rows !== false;
      const rowsValue = Array.isArray(value) ? value : [];
      const currentRows =
        rowsValue.length < minRows
          ? [...rowsValue, ...Array(minRows - rowsValue.length).fill({})]
          : rowsValue;

      const setRows = (nextRows) => onChange(nextRows);
      const updateCell = (rowIdx, colId, v) => {
        const nextRows = currentRows.slice();
        nextRows[rowIdx] = { ...(nextRows[rowIdx] || {}), [colId]: v };
        setRows(nextRows);
      };
      const addRow = () => setRows([...currentRows, {}]);
      const deleteRow = (rowIdx) =>
        setRows(currentRows.filter((_, i) => i !== rowIdx));

      const canAdd =
        !readOnly && allowAddRows && currentRows.length < maxRows;
      const canDelete = (rowIdx) =>
        !readOnly && currentRows.length > minRows && rowIdx >= 0;

      const columnWidth = (col) => {
        if (col.type === "textarea" || col.type === "multiselect") return 180;
        if (col.type === "checkbox" || col.type === "radio") return 200;
        if (col.type === "datetime") return 170;
        if (col.type === "date") return 140;
        if (col.type === "file") return 180;
        return 120;
      };
      const showDeleteColumn =
        allowAddRows && !readOnly && currentRows.length > minRows;

      const columns = [
        ...cols.map((col) => ({
          title: col.label,
          dataIndex: col.id,
          key: col.id,
          width: columnWidth(col),
          render: (_, record) =>
            renderTableCell(
              col,
              record.__row?.[col.id],
              (v) => updateCell(record.__idx, col.id, v),
              readOnly,
              uploadContext
            ),
        })),
        ...(showDeleteColumn
          ? [
              {
                title: "",
                dataIndex: "__actions",
                key: "__actions",
                width: 48,
                render: (_, record) =>
                  canDelete(record.__idx) ? (
                    <Button
                      type="text"
                      size="small"
                      danger
                      onClick={() => deleteRow(record.__idx)}
                    >
                      ✕
                    </Button>
                  ) : null,
              },
            ]
          : []),
      ];

      const data = currentRows.map((row, idx) => ({
        key: idx,
        __idx: idx,
        __row: row || {},
      }));

      const scrollX =
        cols.reduce((sum, col) => sum + columnWidth(col), 0) +
        (showDeleteColumn ? 48 : 0);

      return (
        <Space direction="vertical" size={8} style={FIELD_CONTROL_WIDTH}>
          <Table
            size="small"
            bordered
            pagination={false}
            dataSource={data}
            columns={columns}
            tableLayout="fixed"
            scroll={{ x: scrollX }}
          />
          {canAdd && (
            <Button size="small" onClick={addRow}>
              + Add row
            </Button>
          )}
        </Space>
      );
    }
    case FIELD_TYPES.FILE:
      return (
        <ClinicalFormFileUpload
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          admissionId={uploadContext?.admissionId}
          admissionType={uploadContext?.admissionType}
        />
      );
    default:
      return null;
  }
};

const isSelected = (value, optValue) =>
  Array.isArray(value) ? value.includes(optValue) : value === optValue;

const nestedBlockStyle = {
  marginTop: 12,
  marginLeft: 24,
  paddingLeft: 20,
  borderLeft: "1px solid #f2f4f7",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const NestedFields = ({
  fields,
  values,
  onChange,
  readOnly,
  uploadContext,
  fieldErrors,
  clearFieldError,
  setFieldError,
}) => (
  <div style={nestedBlockStyle}>
    {fields.map((sub) => (
      <Field
        key={sub.id}
        field={sub}
        values={values}
        onChange={onChange}
        readOnly={readOnly}
        uploadContext={uploadContext}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
        setFieldError={setFieldError}
      />
    ))}
  </div>
);

const renderOptionListWithSubs = ({
  field,
  value,
  setValue,
  values,
  onChange,
  readOnly,
  uploadContext,
  fieldErrors,
  clearFieldError,
  setFieldError,
}) => {
  const isRadio = field.type === FIELD_TYPES.RADIO;
  const toggleCheckbox = (optValue, checked) => {
    const selected = Array.isArray(value) ? value : [];
    const set = new Set(selected);
    if (checked) set.add(optValue);
    else set.delete(optValue);
    setValue(Array.from(set));
  };

  const items = (field.options || []).map((opt) => {
    const selected = isSelected(value, opt.value);
    const subs = selected ? opt.sub_questions || [] : [];
    return (
      <React.Fragment key={opt.value}>
        {isRadio ? (
          <Radio value={opt.value}>{opt.label}</Radio>
        ) : (
          <Checkbox
            checked={selected}
            onChange={(e) => toggleCheckbox(opt.value, e.target.checked)}
            disabled={readOnly}
          >
            {opt.label}
          </Checkbox>
        )}
        {subs.length > 0 && (
          <NestedFields
            fields={subs}
            values={values}
            onChange={onChange}
            readOnly={readOnly}
            uploadContext={uploadContext}
            fieldErrors={fieldErrors}
            clearFieldError={clearFieldError}
            setFieldError={setFieldError}
          />
        )}
      </React.Fragment>
    );
  });

  if (isRadio) {
    return (
      <Radio.Group
        value={value}
        onChange={(e) => setValue(e.target.value)}
        style={{ width: "100%" }}
        disabled={readOnly}
      >
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {items}
        </Space>
      </Radio.Group>
    );
  }
  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      {items}
    </Space>
  );
};

const collectSelectOptionSubs = (field, value) => {
  if (
    field.type !== FIELD_TYPES.SELECT &&
    field.type !== FIELD_TYPES.MULTISELECT
  ) {
    return [];
  }
  return (field.options || [])
    .filter(
      (o) =>
        typeof o === "object" &&
        o?.sub_questions?.length &&
        isSelected(value, o.value)
    )
    .flatMap((o) => o.sub_questions);
};

const Field = ({
  field,
  values,
  onChange,
  readOnly,
  uploadContext,
  fieldErrors,
  clearFieldError,
  setFieldError,
}) => {
  if (field.type === FIELD_TYPES.SECTION_HEADER) {
    return (
      <Flex align="center" gap={8} style={{ marginTop: 8 }}>
        <span
          style={{
            width: 3,
            height: 14,
            background: "var(--drf-primary, #7f56d9)",
            borderRadius: 2,
            display: "inline-block",
          }}
        />
        <Typography.Text
          strong
          style={{ fontSize: 12, letterSpacing: "0.06em" }}
        >
          {(field.label || "Group").toUpperCase()}
        </Typography.Text>
      </Flex>
    );
  }

  const value = values[field.id];
  const setValue = (v) => {
    if (
      field.type === FIELD_TYPES.NUMBER &&
      v != null &&
      v !== "" &&
      Number.isNaN(Number(v))
    ) {
      setFieldError?.(field.id, DECIMAL_ERROR_MESSAGE);
      return;
    }
    if (fieldErrors?.[field.id]) clearFieldError?.(field.id);
    onChange(field.id, v);
  };
  const error = fieldErrors?.[field.id];

  const directSubs = field.sub_questions || [];
  const inlinesOptionSubs =
    field.type === FIELD_TYPES.RADIO || field.type === FIELD_TYPES.CHECKBOX;
  const trailingSubs = inlinesOptionSubs
    ? directSubs
    : [...directSubs, ...collectSelectOptionSubs(field, value)];

  return (
    <div style={{ paddingBottom: 4 }}>
      <Typography.Text strong>
        {field.label || "Untitled field"}
        {field.required && (
          <span style={{ color: "#f04438", marginLeft: 4 }}>*</span>
        )}
      </Typography.Text>
      <div style={{ marginTop: 10 }}>
        {inlinesOptionSubs
          ? renderOptionListWithSubs({
              field,
              value,
              setValue,
              values,
              onChange,
              readOnly,
              uploadContext,
              fieldErrors,
              clearFieldError,
              setFieldError,
            })
          : renderControl(field, value, setValue, readOnly, uploadContext, {
              set: (msg) => setFieldError?.(field.id, msg),
              clear: () => clearFieldError?.(field.id),
            })}
      </div>
      {error && (
        <Typography.Text
          type="danger"
          style={{ fontSize: 12, display: "block", marginTop: 8 }}
        >
          {error}
        </Typography.Text>
      )}

      {trailingSubs.length > 0 && (
        <NestedFields
          fields={trailingSubs}
          values={values}
          onChange={onChange}
          readOnly={readOnly}
          uploadContext={uploadContext}
          fieldErrors={fieldErrors}
          clearFieldError={clearFieldError}
          setFieldError={setFieldError}
        />
      )}
    </div>
  );
};

const SectionFields = ({
  section,
  values,
  onChange,
  readOnly,
  uploadContext,
  fieldErrors,
  clearFieldError,
  setFieldError,
}) => (
  <Flex vertical gap={20}>
    {(section.fields || []).map((f) => (
      <Field
        key={f.id}
        field={f}
        values={values}
        onChange={onChange}
        readOnly={readOnly}
        uploadContext={uploadContext}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
        setFieldError={setFieldError}
      />
    ))}
  </Flex>
);

const FormActionBar = ({ label, loading, onClick, readOnly }) =>
  readOnly ? null : (
    <div
      className="dynamic-form-action-bar"
      style={{
        flexShrink: 0,
        background: "#fff",
        padding: "10px 16px calc(10px + env(safe-area-inset-bottom, 0px))",
        borderTop: "1px solid #e8e8e8",
        textAlign: "right",
        zIndex: 5,
        boxSizing: "border-box",
      }}
    >
      <Button
        type="primary"
        loading={loading}
        onClick={onClick}
        className={btnStyles.mdBtn}
        style={{ fontWeight: 500 }}
      >
        {label}
      </Button>
    </div>
  );

const InspectionFormLayout = ({
  sections,
  activeSectionId,
  onSectionChange,
  values,
  onChange,
  readOnly,
  onSubmit,
  saving,
  isEdit,
  uploadContext,
  fieldErrors,
  clearFieldError,
  setFieldError,
  onValidate,
  isDirty,
  onMarkClean,
  confirmExit = false,
}) => {
  const wrapperRef = useRef(null);
  const { token } = theme.useToken();
  const activeSection =
    sections.find((section) => section.id === activeSectionId) ||
    sections[0];
  const activeIndex = sections.findIndex(
    (section) => section.id === activeSection?.id
  );
  const isLastSection = activeIndex === sections.length - 1;
  const showTabs = sections.length > 1;

  const actionLabel = isLastSection
    ? isEdit
      ? "Update"
      : "Submit"
    : "Save & Proceed";

  const handleAction = () => {
    // Mandatory validation applies for all dynamic forms on action button.
    const scope = isLastSection
      ? sections
      : activeSection
        ? [activeSection]
        : sections;
    if (!onValidate?.(scope)) return;
    if (isLastSection) {
      onSubmit?.();
      return;
    }
    const nextSection = sections[activeIndex + 1];
    if (nextSection) {
      onMarkClean?.();
      onSectionChange(nextSection.id);
    }
    wrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Unsaved-changes modal only on IA / Review when dirty
  const handleTabChange = (key) => {
    if (key === activeSectionId) return;
    if (confirmExit && isDirty) {
      Modal.confirm({
        title: "Unsaved changes",
        content:
          "You have unsaved changes. Are you sure you want to save these changes?",
        okButtonProps: {
          style: {
            backgroundColor: token.colorPrimary,
            borderColor: token.colorPrimary,
          },
        },
        onOk: () => {
          const scope = activeSection ? [activeSection] : sections;
          if (!onValidate?.(scope)) return;
          onMarkClean?.();
          onSectionChange(key);
        },
        onCancel: () => {
          onMarkClean?.();
          onSectionChange(key);
        },
      });
    } else {
      onSectionChange(key);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="initial-inspection-form-wrapper dynamic-form-renderer"
      style={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: 0,
      }}
    >
      {showTabs && (
        <Row style={{ flexShrink: 0 }}>
          <Col span={24}>
            <Tabs
              centered
              style={{ width: "100%" }}
              activeKey={activeSectionId}
              items={sections.map((section) => ({
                key: section.id,
                label: section.title || "Section",
              }))}
              onChange={handleTabChange}
            />
          </Col>
        </Row>
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: TINT_BG,
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div className="ip-forms" style={{ paddingBottom: 120 }}>
            <SectionFields
              section={activeSection}
              values={values}
              onChange={onChange}
              readOnly={readOnly}
              uploadContext={uploadContext}
              fieldErrors={fieldErrors}
              clearFieldError={clearFieldError}
              setFieldError={setFieldError}
            />
          </div>
        </div>

        <FormActionBar
          label={actionLabel}
          loading={saving}
          onClick={handleAction}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
};

const DynamicFormRenderer = ({
  sections = [],
  values = {},
  onChange,
  readOnly = false,
  onSubmit,
  saving = false,
  isEdit = false,
  admissionId,
  admissionType = "IP",
  isDirtyRef,
  confirmExit = false,
}) => {
  const [activeSectionId, setActiveSectionId] = useState(sections[0]?.id);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!sections.length) {
      setActiveSectionId(undefined);
      return;
    }
    if (!sections.some((section) => section.id === activeSectionId)) {
      setActiveSectionId(sections[0].id);
    }
  }, [sections, activeSectionId]);

  // expose isDirty to parent via ref so back-button can check it
  useEffect(() => {
    if (isDirtyRef) isDirtyRef.current = isDirty;
  }, [isDirty, isDirtyRef]);

  const setField = (fieldId, v) => {
    if (readOnly) return;
    setIsDirty(true);
    onChange({ ...values, [fieldId]: v });
  };

  const clearFieldError = (fieldId) => {
    setFieldErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const setFieldError = (fieldId, msg) => {
    setFieldErrors((prev) =>
      prev[fieldId] === msg ? prev : { ...prev, [fieldId]: msg }
    );
  };

  const validateAll = (scopeSections = sections) => {
    const missing = findMissingRequired(scopeSections, values);
    if (!missing.length) {
      setFieldErrors({});
      return true;
    }
    const errors = {};
    missing.forEach((m) => {
      errors[m.fieldId] = "This field is required";
    });
    setFieldErrors(errors);
    message.error(formatRequiredError(missing));
    if (missing[0]?.sectionId && missing[0].sectionId !== activeSectionId) {
      setActiveSectionId(missing[0].sectionId);
    }
    return false;
  };

  if (!sections.length) return null;

  const uploadContext = { admissionId, admissionType };

  return (
    <InspectionFormLayout
      sections={sections}
      activeSectionId={activeSectionId}
      onSectionChange={setActiveSectionId}
      values={values}
      onChange={setField}
      readOnly={readOnly}
      onSubmit={onSubmit}
      saving={saving}
      isEdit={isEdit}
      uploadContext={uploadContext}
      fieldErrors={fieldErrors}
      clearFieldError={clearFieldError}
      setFieldError={setFieldError}
      onValidate={validateAll}
      isDirty={isDirty}
      onMarkClean={() => setIsDirty(false)}
      confirmExit={confirmExit}
    />
  );
};

export default DynamicFormRenderer;
